// src/components/Footer.jsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaFacebookF, FaInstagram, FaYoutube, FaTiktok, FaMapMarkerAlt, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';

import footerBgSmMd from '../assets/pick/35.jpg';
import footerBgLg from '../assets/pick/9.jpeg';

const Footer = () => {
    const { t } = useTranslation('footer');

    return (
        <footer className="relative bg-cover bg-center text-white py-16 sm:py-20 lg:py-28 overflow-hidden">
            {/* Background Images */}
            <div
                className="absolute inset-0 bg-cover bg-center lg:hidden"
                style={{ backgroundImage: `url(${footerBgSmMd})` }}
            ></div>
            <div
                className="absolute inset-0 bg-cover bg-center hidden lg:block"
                style={{ backgroundImage: `url(${footerBgLg})`, backgroundAttachment: 'fixed' }}
            ></div>

            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70"></div>

            <div className="container mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
                {/* Main Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16 text-center lg:text-left">

                    {/* Brand & Navigation */}
                    <div className="space-y-8">
                        <h5
                            className="text-4xl lg:text-5xl font-extrabold text-[#96BA33] tracking-tight"
                            dangerouslySetInnerHTML={{ __html: t('brand') }}
                        ></h5>
                        <ul className="space-y-4">
                            <li><NavLink to="/" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.home')}</NavLink></li>
                            <li><NavLink to="/about-us" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.about')}</NavLink></li>
                            <li><NavLink to="/srilanka" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.explore')}</NavLink></li>
                            <li><NavLink to="/tour" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.tours')}</NavLink></li>
                            <li><NavLink to="/gallery" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.gallery')}</NavLink></li>
                            <li><NavLink to="/events" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.events')}</NavLink></li>
                            <li><NavLink to="/things" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.things')}</NavLink></li>
                            <li><NavLink to="/contact-us" className="text-lg text-gray-200 hover:text-cyan-300 transition duration-300">{t('nav.contact')}</NavLink></li>
                        </ul>
                    </div>

                    {/* Get in Touch */}
                    <div className="space-y-8">
                        <h5 className="text-3xl lg:text-4xl font-bold text-[#96BA33] mb-8">{t('contact.title')}</h5>
                        <ul className="space-y-6">
                            <li className="flex items-start justify-center lg:justify-start gap-4">
                                <FaMapMarkerAlt className="w-6 h-6 text-emerald-400 mt-1" />
                                <span className="text-gray-100 text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: t('contact.address') }} />
                            </li>
                            <li className="flex items-center justify-center lg:justify-start gap-4">
                                <FaEnvelope className="w-6 h-6 text-emerald-400" />
                                <a href="mailto:info@starlankatours.com" className="text-lg text-gray-100 hover:text-cyan-300 transition">
                                    {t('contact.email')}
                                </a>
                            </li>
                            <li className="flex items-center justify-center lg:justify-start gap-4">
                                <FaPhoneAlt className="w-6 h-6 text-emerald-400" />
                                <a href="tel:+94768051224" className="text-lg text-gray-100 hover:text-cyan-300 transition">
                                    {t('contact.phone')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Stay Connected & Newsletter */}
                    <div className="space-y-8">
                        <h5 className="text-3xl lg:text-4xl font-bold text-[#96BA33] mb-8">{t('newsletter.title')}</h5>
                        <p className="text-gray-300 text-lg max-w-sm mx-auto lg:mx-0">
                            {t('newsletter.desc')}
                        </p>

                        {/* Newsletter Form */}
                        <div className="mt-6">
                            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto lg:mx-0">
                                <input
                                    type="email"
                                    placeholder={t('newsletter.placeholder')}
                                    className="flex-1 px-6 py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition text-lg"
                                />
                                <button className="px-8 py-4 bg-[#96BA33] rounded-full font-bold uppercase tracking-wider hover:from-cyan-400 hover:to-emerald-400 transform hover:scale-105 transition duration-300 shadow-xl">
                                    {t('newsletter.subscribe')}
                                </button>
                            </form>
                        </div>

                        {/* Social Icons */}
                        <div className="flex justify-center lg:justify-start gap-8 mt-10">
                            <a href="https://web.facebook.com/" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center hover:bg-cyan-500 transition transform hover:scale-110">
                                <FaFacebookF size={24} />
                            </a>
                            <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center hover:bg-gradient-to-br from-purple-500 to-pink-500 transition transform hover:scale-110">
                                <FaInstagram size={26} />
                            </a>
                            <a href="https://youtube.com/" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center hover:bg-red-600 transition transform hover:scale-110">
                                <FaYoutube size={26} />
                            </a>
                            <a href="https://www.tiktok.com/" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center hover:bg-black transition transform hover:scale-110">
                                <FaTiktok size={24} />
                            </a>
                        </div>
                    </div>

                    {/* Tagline (Desktop Only) */}
                    <div className="hidden lg:block space-y-6">
                        <p className="text-2xl font-light italic text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('tagline') }} />
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-16 pt-10 border-t border-gray-700 text-center">
                    <p className="text-gray-400 text-sm sm:text-base">
                        © {new Date().getFullYear()} <span className="text-[#96BA33] font-semibold">Star Lanka Tours Sri Lanka</span>. {t('copyright')}
                    </p>
                    <p className="text-gray-500 text-xs sm:text-sm mt-3">
                        {t('developedBy')} <strong>Redev Solutions</strong> (+94 76 460 8225)
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;